var _jsxFileName = "/opt/atlassian/pipelines/agent/build/src/components/auth/Login/index.tsx";
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { IconQuestionmark } from '@adc/parallax-icons';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ApiStatus } from 'Enums';
import { useApiError, useAuth } from 'Hooks';
import { Banner, Footer, Form, Header, Input, InputPassword, Link, LogoActionBar, Main, ScrollContainer, Spinner } from 'Components/utility';
import { ANALYTICS_ACTIONS, ANALYTICS_EVENTS, ANALYTICS_MODULE_TYPES, ANALYTICS_SOURCE_TYPES } from 'Constants/analytics';
import { AUTH_SESSION } from 'Reducers/auth/types';
import * as analyticsManager from 'Utilities/analytics/analyticsManager';
import { notifyApp } from 'Utilities/appCommunicator';
import { setUrls } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { persistAuthSession } from 'Utilities/session';
import { store } from 'Utilities/store';
import { getLegacyAuthSession, getLegacyToken, signIn } from 'Services/auth';
const initialValues = {
  email: '',
  password: ''
};
const mapStateToProps = ({
  env: {
    app
  },
  config
}) => {
  return {
    app,
    config
  };
};
const connector = connect(mapStateToProps);
const Login = ({
  app,
  config
}) => {
  const {
    showApiErrorModal
  } = useApiError();
  const {
    handleLoginError,
    handleLockoutError,
    showCountryRegionError,
    showHcpError,
    isLocked,
    lockoutMessage,
    lockoutSubmessage
  } = useAuth();
  const validationSchema = Yup.object().shape({
    email: Yup.string().email(i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail')).required(i18n.t('Login.content.signInForm.formField.email.errors.required')),
    password: Yup.string().required(i18n.t('Login.content.signInForm.formField.email.errors.required'))
  });
  const handleWrongRegionError = useCallback((error, handleSubmit) => {
    const unsupportedCountry = error.response?.data.errors[0]?.unsupportedCountry;
    if (!unsupportedCountry) {
      showApiErrorModal();
      return;
    }
    setUrls(config, unsupportedCountry);
    handleSubmit();
  }, [config, showApiErrorModal]);
  const handleApiRequestError = useCallback((error, resetForm, handleSubmit) => {
    const data = error.response?.data ? error.response.data : {
      details: i18n.t('Global.modals.errorCommunicatingServer.body')
    };
    notifyApp('adc-webview:signin', {
      status: error.status,
      data
    });
    switch (error.status) {
      case ApiStatus.UNAUTHORIZED:
        {
          const code = error.response?.data.code;
          if (code === ApiStatus.FORBIDDEN_HCP_ACCOUNT) {
            showHcpError();
            return;
          }
          handleLoginError(error, resetForm);
          break;
        }
      case ApiStatus.BAD_REQUEST:
        {
          const code = error.response?.data.code;
          if (code === ApiStatus.UNSUPPORTED_COUNTRY) {
            showCountryRegionError();
            return;
          }
          if (code === ApiStatus.WRONG_REGION) {
            handleWrongRegionError(error, handleSubmit);
            return;
          }
          showApiErrorModal();
          break;
        }
      case ApiStatus.TOO_MANY_REQUESTS:
        {
          handleLockoutError(error);
          resetForm();
          break;
        }
      default:
        showApiErrorModal();
    }
  }, [showApiErrorModal, handleLoginError, showHcpError, showCountryRegionError, handleWrongRegionError, handleLockoutError]);
  const onSubmit = useCallback(async (values, {
    setSubmitting,
    resetForm,
    submitForm
  }) => {
    try {
      setSubmitting(true);
      const {
        email,
        password
      } = values;
      const {
        accessToken,
        accountId
      } = await signIn(email, password.trim(), app);
      const legacyToken = await getLegacyToken(app, accountId, accessToken);
      const authSession = await getLegacyAuthSession(legacyToken);
      persistAuthSession(authSession);
      store.dispatch({
        type: AUTH_SESSION,
        authSession
      });
      notifyApp('adc-webview:signin', authSession);
      mediator.publish('router:navigate', '/connections');
      analyticsManager.track(ANALYTICS_EVENTS.WEBVIEW_DISPLAYED, {
        module: ANALYTICS_MODULE_TYPES.CONAPPS_MODULE,
        action: ANALYTICS_ACTIONS.DISPLAY,
        source: ANALYTICS_SOURCE_TYPES.EXTERNAL_PARTNER
      });
    } catch (err) {
      const error = err;
      handleApiRequestError(error, resetForm, submitForm);
    } finally {
      setSubmitting(false);
    }
  }, [app, handleApiRequestError]);
  const handleClickForgotPass = useCallback(() => {
    mediator.publish('router:navigate', '/forgot-password');
  }, []);
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    isSubmitting,
    isValid,
    errors,
    touched,
    values
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    validateOnBlur: true,
    initialErrors: {
      email: i18n.t('Login.content.signInForm.formField.email.errors.emailAddressValidEmail'),
      password: i18n.t('Login.content.signInForm.formField.email.errors.required')
    }
  });
  return /*#__PURE__*/React.createElement(Main, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 235,
      columnNumber: 5
    }
  }, /*#__PURE__*/React.createElement(LogoActionBar, {
    testID: "Login",
    title: i18n.t('Login.title'),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 236,
      columnNumber: 7
    }
  }), isSubmitting && /*#__PURE__*/React.createElement(Spinner, {
    testID: "Login",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 237,
      columnNumber: 24
    }
  }), /*#__PURE__*/React.createElement(ScrollContainer, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 238,
      columnNumber: 7
    }
  }, /*#__PURE__*/React.createElement(Header, {
    testID: "Login",
    title: i18n.t('Login.title'),
    subtitle: i18n.t('Login.subtitle', {
      appName: i18n.t('Global.appNames.libreView')
    }),
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 239,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(Banner, {
    testID: "Login",
    message: lockoutMessage,
    submessage: lockoutSubmessage,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 246,
      columnNumber: 9
    }
  }), /*#__PURE__*/React.createElement(Form, {
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 247,
      columnNumber: 9
    }
  }, /*#__PURE__*/React.createElement(Input, {
    "aria-label": i18n.t('Login.content.signInForm.formField.email.label'),
    placeholder: i18n.t('Login.content.signInForm.formField.email.placeholder'),
    label: i18n.t('Login.content.signInForm.formField.email.label'),
    errorMessage: errors.email,
    isInvalid: errors.email && touched.email ? true : false,
    value: values.email,
    testID: "email",
    onChangeText: handleChange('email'),
    onBlur: handleBlur('email'),
    inputMode: "email",
    disabled: isLocked,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 248,
      columnNumber: 11
    }
  }), /*#__PURE__*/React.createElement(InputPassword, {
    placeholder: i18n.t('Login.content.signInForm.formField.password.placeholder'),
    label: i18n.t('Login.content.signInForm.formField.password.label'),
    errorMessage: errors.password,
    testID: "password",
    setFieldValue: setFieldValue,
    onBlur: handleBlur('password'),
    onChangeText: handleChange('password'),
    value: values.password,
    isEyeIconVisible: !isLocked,
    isInvalid: errors.password && touched.password ? true : false,
    isDisabled: isLocked,
    marginTop: "$4",
    marginBottom: "$4",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 261,
      columnNumber: 11
    }
  }), /*#__PURE__*/React.createElement(Link, {
    style: {
      marginTop: 10
    },
    testID: "Login.forgotPassword",
    onPress: handleClickForgotPass,
    IconElement: IconQuestionmark,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 276,
      columnNumber: 11
    }
  }, i18n.t('Login.content.signInForm.secondaryText')))), /*#__PURE__*/React.createElement(Footer, {
    buttonText: i18n.t('Login.title'),
    onButtonSubmit: handleSubmit,
    testIDButton: "Login.signIn",
    isButtonDisabled: isLocked || !isValid,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 286,
      columnNumber: 7
    }
  }));
};
export default connector(Login);